import scopeLogger from 'lib/scope-logger.js';
import Kibana from '#/utils/trackKibana.js';
import { readableDate } from '#/utils/formatters.js';
import { RequestError } from '#/lib/errors/errors.js';

const console = scopeLogger('RMI:InteractiveEvent');

/**
 *
 * @param origin
 * @param binding
 * @param interactive_id
 * @param video_id
 * @return {InteractiveEvent}
 * @constructor
 */
export function createInteractiveEvent(
  origin,
  binding,
  interactive_id,
  video_id,
) {
  const kibana = new Kibana('InteractiveEvent');

  const {
    expires_at: expires = Infinity,
    start_at: start = -Infinity,
    finish_at: end = Infinity,
    duration = 0,
  } = binding;
  const interval = [start, end];

  let _state = 'idle';
  let _creative;
  let _trackingData = null;
  let _renderer;

  const rotatorCallback = (url, retry = 0) => {
    console.info('Rotator callback', url);
    window.fetch(url).then(
      (response) => {
        if (!response.ok) {
          throw new RequestError(response);
        }
      },
      (error) => {
        console.error('Failed to send tracking', error);
        if (retry < 5) {
          setTimeout(rotatorCallback, 5_000 * 2 ** retry, url, retry + 1);
        }
      },
    );
  };

  const listeners = new Map();

  kibana.setContext({
    interactive_id,
    binding,
    video_id,
  });

  const expiresAt = expires === 4_294_967_295 ? Infinity : expires * 1e3;
  let expireTimeout;
  if (Number.isFinite(expiresAt)) {
    expireTimeout = setTimeout(() => {
      event.state = 'expired';
    }, Math.min(2_147_483_647, expiresAt - Date.now()));
  }
  const event = {
    origin,
    binding,
    interactive_id,
    interval,
    /* Backend uses that value (unsigned int) for indicate empty property */
    expiresAt,
    duration,
    videoId: video_id,

    displayBlockers: new Set(),

    get state() {
      return _state;
    },

    /**
     * @param {string} newState - new state for event
     */
    set state(newState) {
      if (_state === 'deleted') {
        console.warn(`Event is deleted, state change to "${newState}" is ignored`, event,);
        return;
      }
      console.log(
        `Event ${event.binding.id} state changed ${_state} -> ${newState}`,
      );
      switch (newState) {
        case 'expired':
          break;
        case 'pending':
          break;
        case 'deleted':
          clearTimeout(expireTimeout);
          break;
        case 'display':
          break;
        case 'failed':
          break;
        case 'shown':
          break;
        case 'idle':
          // reset all creative related data
          event.target = null;
          event.creative = null;
          event.renderer = null;

          break;
        default:
      }

      kibana.setContext({
        eventState: newState,
      });
      const oldState = _state;
      _state = newState;
      if (listeners.has(`state:${newState}`)) {
        listeners.get(`state:${newState}`).forEach((callback) => callback(event, oldState));
      }
    },

    on(eventName, callback) {
      if (!listeners.has(eventName)) {
        listeners.set(eventName, new Set());
      }
      listeners.get(eventName).add(callback);
      return () => this.off(eventName, callback);
    },

    off(eventName, callback) {
      if (listeners.has(eventName)) {
        listeners.get(eventName).delete(callback);
      }
    },

    set renderer(renderer) {
      kibana.setContext({
        renderer: renderer?.name,
      });
      _renderer = renderer;
    },

    get renderer() {
      return _renderer;
    },

    set target(rotatorResponse) {
      _trackingData = rotatorResponse?.tracking_data;
      kibana.setContext({
        campaign: {
          id: _trackingData?.entity_meta?.campaign?.id,
          title: _trackingData?.entity_meta?.campaign?.title,
        },
      });
      _creative = {
        url: rotatorResponse?.url,
      };
    },

    set creative(value) {
      // keep original url in the creative for a tracking
      Object.assign(_creative, value);
      kibana.setContext({
        creative: {
          id: value?.id,
          title: value?.title,
        },
      });
      Object.defineProperties(_creative, {
        dispatch: {
          get() {
            return event.dispatch;
          },
          configurable: true,
        },
        meta: {
          get() {
            return event.meta;
          },
          configurable: true,
        },
      });
    },

    get creative() {
      return _creative;
    },

    get meta() {
      return _trackingData?.entity_meta;
    },

    dispatch: {
      hit() {
        kibana.info('hit event');
        (_trackingData?.hit_event ?? []).forEach(rotatorCallback);
      },
      prepare() {
        event.renderer.locked = true;
        event.displayBlockers.clear();
        event.displayBlockers.add('preparation');
        event.state = 'prepare';
      },
      show() {
        event.renderer.locked = true;
        event.state = 'display';
        kibana.log('display event');
        event.displayBlockers.clear();
        (_trackingData?.show_event ?? []).forEach(rotatorCallback);
      },
      click() {
        (_trackingData?.click_event ?? []).forEach(rotatorCallback);
      },
      close() {
        event.renderer.locked = false;
        event.state = 'shown';
      },
      error(error) {
        // unlock renderer only when event's creative is displayed,
        // or when error happened during prepare
        if (['display', 'prepare'].includes(event.state)) {
          event.renderer.locked = false;
        }
        kibana.error(error);
        event.state = 'failed';
      },
      delete() {
        event.state = 'deleted';
      },
      // special case for delete: user leave from object
      leaveObject() {
        if (['content requested', 'pending', 'prepare', 'await'].includes(event.state)) {
          kibana.warn('user leave object');
        }
        event.state = 'deleted';
      },
      /**
       * @emits {log} Warning if player leave slot without display
       * @emits {idle} update event state in any case
       * @param {number} [positionDelta] - optional, the difference between the new player position and the binding end. Need to understand if there was a reach with normal viewing or not in one of pending states
       */
      leaveSlot(positionDelta) {
        console.log('leave slot', positionDelta);
        if (0 < positionDelta && positionDelta <= 2) {
          /* We should care only about leaving slot with normal viewing
           * AND only for one of "pending" states */
          const blockers = event.displayBlockers.size ?
            Array.from(event.displayBlockers)
            : undefined;

          kibana.warn('reach slot end without display', { blockers });
        }
        event.state = 'idle';
      },
    },
  };

  if (process.env.NODE_ENV === 'development') {
    event._interval = [
      readableDate(event.interval[0] * 1e3),
      readableDate(event.interval[1] * 1e3),
    ];
    event._expiresAt = readableDate(expires * 1e3);
  }

  return event;
}

/**
 * @typedef {number} StartTimestamp
 * @typedef {number} EndTimestamp
 */
/**
 * @typedef {Object} CreativeRef
 * @property {string} url
 * @property {string} id
 * @property {number} idHash
 */
/**
 * @typedef {Object} RotatorTracking
 * @property {() => void} hit
 * @property {() => void} show
 * @property {() => void} click
 */
/**
 * @typedef {Object} RotatorEntity
 * @property {string} id
 * @property {number} id_hash
 * @property {string} title
 */
/**
 * @typedef {Object} RotatorMeta
 * @property {RotatorEntity} campaign
 * @property {RotatorEntity} creative
 * @property {RotatorEntity} landing
 */
/**
 * @typedef {Object} InteractiveEvent
 * @property {string} origin Topic where from event origin
 * @property {CreativeRef|null} creative
 * @property {RotatorMeta} meta
 * @property {RotatorTracking} dispatch
 * @property {[StartTimestamp, EndTimestamp]} interval Interval where event should be triggered, in seconds
 * @property {number} duration
 * @property {string} videoId
 * @property {'idle'|'pending'|'await'|'skipped'|'display'|'shown'|'deleted'|'failed'} state
 * @property {number} expiresAt Timestamp when event should be deleted, in milliseconds
 */
